<template>
  <v-card
    ref="simview"
    elevation="6"
    :class="['simulation-view-container', { collapsed: !isRunning }]"
    :style="simulationViewStyle"
  >
    <simulation-toolbar
      ref="playerbar"
      :is-starting="isStarting"
      :is-running="isRunning"
      @play-clicked="onStart()"
      @stop-clicked="onStop()"
      @restart-clicked="onRestart()"
    />
    <div class="simulation-view-content">
      <execution-context
        v-if="isRunning"
        :context="context"
        @event-raised="eventRaised($event.fqName, $event.value)"
        @value-changed="valueChanged($event.fqName, $event.value)"
      />
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { Highlighter } from "../Editor/services/highlighter";
import { isNumber, SimulationContext } from "../Editor/services/simulation/simulation-control";
import ExecutionContext from "./ExecutionContext.vue";
import SimulationToolbar from "./SimulationToolbar.vue";
import DragHandler from "../Editor/services/drag-handler";
import { MessageEventBus, ModelEventBus, SimulationEventBus } from "../event-bus";
import { debounce } from "lodash-es";
import { ref, inject, onMounted, onBeforeUnmount, computed, reactive } from "vue";
import { editorStateStore } from "../Editor/services/storage/editor-state-store";
import { getModelAsJson } from "../Editor/services/model-helper";
import { DiagnosticSeverity, ValidatorService } from "../Editor/services/validation-service";
import RappidContext from "../Editor/rappid/rappid-context";
import { CodeSimulationService } from "../Editor/services/simulation/code/simulation-service-code";
import { CardPosition } from "../Editor/utils/resizeCard";
import { StatechartsService } from "../Editor/services/statecharts/statecharts-service";

const simview = ref(null);
const playerbar = ref(null);

const isStarting = ref(false);
const isRunning = ref(false);

const context = ref<SimulationContext>(new SimulationContext());

const simulationControl: CodeSimulationService = inject<CodeSimulationService>("simulationControl");

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");

const validatorService: ValidatorService = inject<ValidatorService>("validatorService");

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const highlighter: Highlighter = inject<Highlighter>("highlighter");

const warnModelChanged = debounce(() => {
  if (isRunning.value)
    MessageEventBus.emit("warning", {
      text: "Model changed during simulation, restart required.",
      id: "simulation-service-code",
      showProgress: false,
      action: {
        label: "Restart",
        handler: onRestart,
      },
    });
}, 1000);

const position = reactive<CardPosition>({
  xLeft: "unset",
  xRight: "unset",
  yTop: "unset",
  yBottom: "unset",
});

const simulationViewStyle = computed(() => {
  return {
    left: position.xLeft,
    top: position.yTop,
  };
});

onMounted(() => {
  simulationControl
    .addOnStateEntered((data) => highlighter.stateEntered(data.stateId))
    .addOnStateExited((data) => highlighter.stateExited(data.stateId))
    .addOnReactionFired((stateId, transId) => highlighter.reactionFired(stateId, transId))
    .addOnValueChanged((varId, value, varIdProp) => updateValue(varId, value, varIdProp));

  ModelEventBus.on("modelChanged", warnModelChanged);

  SimulationEventBus.on("stopSimulation", onStop);
  const dragTarget = playerbar.value.dragicon.$el as HTMLElement;
  const dragContent = simview.value.$el as HTMLElement;
  new DragHandler(dragTarget, dragContent, position).enableDragging();
});

onBeforeUnmount(() => {
  ModelEventBus.off("modelChanged", warnModelChanged);
  SimulationEventBus.off("stopSimulation", onStop);
});

function onStart() {
  isStarting.value = true;
  const payload: string = getModelAsJson(rappidContext.paper.model);
  cloudStatechartService
    .createOrUpdate(editorStateStore().getModelId, payload, rappidContext)
    .then(() => {
      validatorService.validateModelById(
        editorStateStore().getModelId,
        async (diagnostics) => {
          const errors = diagnostics.filter((d) => d.severity === DiagnosticSeverity.ERROR);
          if (errors && errors.length > 0) {
            isStarting.value = false;
            MessageEventBus.emit("error", {
              id: "simulation-service-code",
              text: "Cannot start simulation: Model is empty or contains errors.",
            });
          } else {
            await simulationControl.load(editorStateStore().getModelId, resetSimulation);
            simulationControl.setup();
            simulationControl.enter(simulationStartedCallback);
          }
        },
        resetSimulation
      );
    })
    .catch(resetSimulation);
}

async function onStop() {
  await simulationControl.exit(simulationExitedCallback);
  simulationControl.tearDown();
  resetSimulation();
}

function resetSimulation() {
  isStarting.value = false;
  isRunning.value = false;

  // If the card was manually resized it may have inline width/height styles.
  // Clear them so the CSS collapsed rule can take effect and the card shrinks.
  try {
    const el = simview.value?.$el as HTMLElement | undefined;
    if (el) {
      el.style.width = "";
      el.style.height = "";
      el.style.minWidth = "";
      el.style.minHeight = "";
    }
  } catch (e) {
    /* ignore if ref not available */
  }
}

async function onRestart() {
  await onStop();
  onStart();
}

function simulationStartedCallback(newContext: SimulationContext) {
  context.value = newContext;
  isRunning.value = true;
  isStarting.value = false;
}

function simulationExitedCallback() {
  highlighter.clearAll();
}

//eslint-disable-next-line @typescript-eslint/no-explicit-any
function eventRaised(fqName: string, value: any) {
  simulationControl.raiseEvent(fqName, value);
}

//eslint-disable-next-line @typescript-eslint/no-explicit-any
function valueChanged(fqName: string, value: any) {
  simulationControl.changeValue(fqName, value);
  updateValue(fqName, value);
}

//eslint-disable-next-line @typescript-eslint/no-explicit-any
function updateValue(featureId: string, value: any, featureIdProp?: string) {
  const slot = featureIdProp ? context.value.findBy(featureIdProp, featureId) : context.value.findByFqn(featureId);
  if (slot) {
    slot.value = isNumber(slot) ? +value : value;
  }
}
</script>
<style scoped>
.simulation-view-container {
  position: absolute;
  z-index: 1;
  resize: both;
  overflow: auto;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.simulation-view-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.simulation-view-container.collapsed {
  /* Force the card to size to its content (toolbar) when collapsed */
  width: auto !important;
  height: auto !important;
  min-width: 0 !important;
  min-height: 0 !important;
}
</style>
